/**
 * The web client's permission transport (P2 — INV-104).
 *
 * Permissions used to ride the Firebase ID token (`useRBAC` read
 * `getIdTokenResult().claims.permissions`). A token lives ~1 hour and the hook
 * force-refreshes it only on mount, so a revoked grant stayed VISIBLE in the UI
 * for up to an hour while the backend 403'd the action — buttons that exist and
 * fail, which is worse than either consistent state. `GET /users/me` now carries
 * the effective set the backend resolved from Postgres on that same request.
 *
 * Why a module and not a React Query hook: `useRBAC` has ~45 consumers, many of
 * them rendered by component tests that mount WITHOUT a `QueryClientProvider`.
 * Adding `useQuery` inside the hook would make a provider a hard requirement of
 * every one of those tests. This module gives the same property React Query was
 * wanted for — one request per screen — with none of that coupling:
 *   - concurrent callers share one IN-FLIGHT promise (single flight),
 *   - a resolved set is reused for ME_PERMISSIONS_TTL_MS,
 *   - a REJECTION is never memoised (offline must retry on the next mount, not
 *     be denied for the whole TTL),
 *   - a TRANSIENT failure gets one bounded retry before it rejects, because the
 *     caller fails closed and a fail-closed set looks exactly like "you hold
 *     nothing" to the user,
 *   - and if it still fails, an answer already published for the SAME uid is KEPT
 *     (only the `unavailable` flag and its class change) — with one shared answer,
 *     one dropped re-read would otherwise blank every affordance on the screen,
 *   - the memo is uid-keyed, so it can never hand one user another's set.
 *
 * It is also the SHARED STATE for the answer (R-E), not just a cache of it.
 * `useRBAC` is a hook with local state, so each of its ~45 consumers used to hold
 * its own verdict: a consumer mounting after the 30 s TTL did its own read, and
 * the two could disagree — one consumer silently hiding every affordance while the
 * banner (owned by the layout's instance) said nothing, or the banner claiming
 * actions were hidden when they were not. Every instance now subscribes to the ONE
 * `PermissionState` published here, so the screen cannot contradict itself.
 *
 * ⚠ Only ever called with a NON-NULL Firebase user (see `useRBAC`). That is what
 * keeps it clear of the documented effect-ordering trap in `utils/api.ts:17-25`:
 * child effects run before parent effects, so a request fired on first paint can
 * beat AuthProvider's Bearer-injecting request interceptor. `user` is null until
 * `onAuthStateChanged` fires — long after the interceptor is installed — and the
 * DEV_BYPASS path (the one case where a user exists on the first render) never
 * calls this at all.
 */
import api from '../utils/api';
import type { UserClaims } from './useRBAC';
import { isSessionExpiredError } from './sessionPolicy';

/** How long a fetched set is reused. Short on purpose: the point of moving off
 *  the token is that a permission edit binds on the next read, and the backend's
 *  own per-process cache is 10 s. */
export const ME_PERMISSIONS_TTL_MS = 30_000;

/** Gap before the single retry below. Long enough to outlive a dropped socket or
 *  a restarting instance, short enough that a signed-in user never watches it.
 *  ⚠ A test that FAKE-TIMES and lets the read fail must advance timers, or the
 *  retry never fires. */
export const ME_PERMISSIONS_RETRY_DELAY_MS = 250;

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Is this failure worth ONE more attempt?
 *
 * No HTTP response at all (offline, DNS, TLS, abort, timeout) or the server
 * saying "not now" (408/429/5xx) — those are blips. Every other 4xx is the
 * backend's ANSWER: a coded 401 (`USER_INACTIVE` / `USER_NOT_PROVISIONED`) is
 * already being handled by AuthProvider's sign-out interceptor, and retrying it
 * would only race that sign-out and double the noise.
 */
function isTransient(error: unknown): boolean {
  const status = (error as { response?: { status?: number } } | undefined)?.response?.status;
  if (typeof status !== 'number') return true;
  return status === 408 || status === 429 || status >= 500;
}

/**
 * The effective set from `GET /users/me`, or `null` when the response carries no
 * `permissions` ARRAY at all.
 *
 * ⚠ Absent is NOT empty. Resolving a missing key to `[]` made a non-answer look like a known
 * empty grant and wrote it over the offline cache. P4 stopped minting the ID-token fallback, so
 * `null` is now definitive: fail closed and leave this user's uid-safe offline cache untouched.
 * A non-array payload is treated the same way.
 */
async function readPermissions(): Promise<string[] | null> {
  const res = await api.get('/users/me');
  const raw = (res as { data?: { permissions?: unknown } } | undefined)?.data?.permissions;
  return Array.isArray(raw) ? raw.filter((p): p is string => typeof p === 'string') : null;
}

/** Where the published set came from. P4 removed the token source with the claim it read. */
export type PermissionSource = 'live' | 'cache';

/**
 * Why the set could not be read, which is what the banner's WORDING turns on:
 *  - `'transient'` — no HTTP response (offline, DNS, TLS, abort, timeout) or the
 *    server said "not now" (408/429/5xx, including FW1's 503
 *    AUTH_BACKEND_UNAVAILABLE). Reloading can genuinely fix it.
 *  - `'definitive'` — the transport declined to retry: any other 4xx (a 403, the
 *    clinic-drift 401 that survived the re-auth) or a 200 with no permissions
 *    array. Reloading cannot fix it, so the banner must not promise it can.
 */
export type PermissionFailureClass = 'transient' | 'definitive';

export interface PermissionState {
  uid: string | null;
  /** `null` = no answer at all (never "holds nothing" — see `unavailable`). */
  permissions: string[] | null;
  source: PermissionSource | null;
  /** The set could not be READ. ⚠ Not the same as "there is no set": a TRANSIENT
   *  failure keeps the last answer for this uid (see `publishPermissionsUnavailable`),
   *  so `unavailable: true` with a non-null `permissions` means "still usable, not
   *  re-confirmed". A definitive refusal is `unavailable: true` + `null`. */
  unavailable: boolean;
  failure: PermissionFailureClass | null;
}

/** No answer for anyone yet: the initial state, and what a reset restores. */
const NO_ANSWER: PermissionState = {
  uid: null,
  permissions: null,
  source: null,
  unavailable: false,
  failure: null,
};

let state: PermissionState = NO_ANSWER;
const listeners = new Set<() => void>();

/** The one published answer. Stable identity between changes (it is read through
 *  `useSyncExternalStore`, which compares snapshots by reference). */
export function getPermissionState(): PermissionState {
  return state;
}

export function subscribePermissionState(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function notify(): void {
  listeners.forEach((fn) => fn());
}

function sameSet(a: string[] | null, b: string[] | null): boolean {
  if (a === b) return true;
  if (a === null || b === null) return false;
  return a.length === b.length && a.every((p, i) => p === b[i]);
}

/** Publish, unless nothing actually changed — two consumers resolving the same
 *  cached set must not keep re-rendering every subscriber. */
function publish(next: PermissionState): void {
  if (
    state.uid === next.uid &&
    state.source === next.source &&
    state.unavailable === next.unavailable &&
    state.failure === next.failure &&
    sameSet(state.permissions, next.permissions)
  ) {
    return;
  }
  state = next;
  notify();
}

/**
 * A set `useRBAC` resolved WITHOUT this transport: the uid-keyed offline claims
 * cache (A5). Published here so the late-mounting consumer and the banner see the
 * same answer as the instance that did the work.
 */
export function publishCachedPermissions(uid: string, permissions: string[]): void {
  // A superseded read may fail after provisioning publishes a live answer.
  if (state.uid === uid && state.source === 'live' && !state.unavailable) return;
  publish({ uid, permissions, source: 'cache', unavailable: false, failure: null });
}

/**
 * "We could not read your permissions" — the fail-closed state, said out loud.
 * Callers that never even attempted the read (known-offline with no cached set)
 * publish it themselves; a failed read publishes it from inside `fetchMePermissions`.
 *
 * ⚠ A TRANSIENT failure does NOT discard an answer we already have for this uid.
 * Sharing one answer app-wide (R-E) also shares the failures: after the 30 s TTL a
 * late-mounting consumer re-reads, and publishing `permissions: null` on that one
 * dropped request made every affordance disappear for EVERY mounted consumer
 * mid-procedure — where before the shared store each already-resolved consumer kept
 * working. A blip is not evidence about the grant, so the last live/token/cache
 * answer stays and only `unavailable` + `failure` change; the banner's transient
 * copy says the set could not be CHECKED, not that anything is hidden.
 *
 * A DEFINITIVE refusal is the opposite: a 403, the clinic-drift 401 that survived
 * the re-auth, or a 200 with no array IS an answer about this identity, so the set
 * it replaces can no longer be trusted and the UI fails closed (INV-104).
 *
 * A different uid never retains anything — that would hand one user the previous
 * user's affordances.
 */
export function publishPermissionsUnavailable(
  uid: string,
  failure: PermissionFailureClass,
): void {
  if (failure === 'transient' && state.uid === uid && state.permissions !== null) {
    publish({ ...state, unavailable: true, failure });
    return;
  }
  publish({ uid, permissions: null, source: null, unavailable: true, failure });
}

/* ────────────────────────────────────────────────────────────────────────────
 * THE OTHER HALF OF THE ANSWER: WHO THIS IS.
 *
 * The permission SET moved into this module (R-E, above) because ~45 consumers render it and a
 * screen that contradicts itself is worse than either answer. The IDENTITY — `role` + `clinicId`,
 * which the ID token alone decides — stayed per-instance, and that asymmetry was the BLOCKING
 * defect of the no-access work (INV-108's fix wave): every `useRBAC()` consumer ran its own
 * `getIdTokenResult`, so a fresh one had `claims === null` until ITS OWN round trip resolved, even
 * when another instance on the same screen was fully resolved and the store was warm. Three things
 * followed from that one window, and only the first was ever visible:
 *
 *   1. every Add/Edit/Delete on every page, and the whole Dashboard, painted as "holds nothing"
 *      and then popped in — on every page visit, for every user, including an Owner;
 *   2. entering a gated screen cost TWO serial token round trips (the wrapper's, then the page's);
 *   3. and when the refresh REJECTED with offline mode off, the hook produced `!loading &&
 *      claims === null` — a THIRD state, "identity unknown", which `screenAccessVerdict` was
 *      answering `deny`: "You don't have access to view clients", to somebody who has it.
 *
 * So the identity is published here too, under the same rules the set already follows: uid-keyed
 * (a surviving snapshot must never answer for a different person), cleared by `resetMePermissions`
 * on sign-out and session end, and read through `useSyncExternalStore` so a late mount paints the
 * known identity on its FIRST render instead of a null.
 *
 * ⚠ IT IS A SEED, NOT A CACHE THAT REPLACES THE READ. Every instance still runs its own token
 * refresh in the background — the store removes the blank first paint, it does not decide
 * freshness. And a refresh that FAILS while an identity for the same uid is already known KEEPS
 * it, exactly as `publishPermissionsUnavailable`'s transient arm keeps a set: a blip is not
 * evidence about who someone is. With nothing known, there is no identity and no refusal either —
 * that is the "identity unknown" state, and INV-107's rule is that it decides nothing.
 * ──────────────────────────────────────────────────────────────────────────── */

/** What the ID token decides, and all this store ever holds. Never permissions — those have their
 *  own lifecycle above (a token is a ~1-hour snapshot; the set is re-read per screen). */
export interface SharedIdentity {
  readonly role: UserClaims['role'];
  readonly clinicId: string;
}

export interface IdentityState {
  /** Whose identity this is. `null` = nobody's; a read for any other uid must ignore it. */
  readonly uid: string | null;
  readonly identity: SharedIdentity | null;
}

/** No identity for anyone: the initial state, and what a reset restores. */
const NO_IDENTITY: IdentityState = { uid: null, identity: null };

let identityState: IdentityState = NO_IDENTITY;
const identityListeners = new Set<() => void>();

/** Stable identity between changes — read through `useSyncExternalStore`, which compares by
 *  reference, so returning a fresh object here would re-render every consumer on every render. */
export function getIdentityState(): IdentityState {
  return identityState;
}

export function subscribeIdentityState(listener: () => void): () => void {
  identityListeners.add(listener);
  return () => {
    identityListeners.delete(listener);
  };
}

function notifyIdentity(): void {
  identityListeners.forEach((fn) => fn());
}

/**
 * Publish the identity this instance just resolved, for every other instance to seed from.
 *
 * Value-compared before publishing: ~45 consumers mount and each re-resolves the same token, so
 * publishing an equal identity on every one of them would re-render the whole tree for nothing.
 */
export function publishIdentity(uid: string, identity: SharedIdentity): void {
  if (
    identityState.uid === uid &&
    identityState.identity?.role === identity.role &&
    identityState.identity?.clinicId === identity.clinicId
  ) {
    return;
  }
  identityState = { uid, identity };
  notifyIdentity();
}

/** Sign-out / session end / no Firebase user. Authz metadata must not outlive the session, and an
 *  identity is exactly that — it names a clinic. */
export function clearIdentity(): void {
  if (identityState === NO_IDENTITY) return;
  identityState = NO_IDENTITY;
  notifyIdentity();
}

interface Snapshot {
  uid: string;
  permissions: string[];
  at: number;
}

let snapshot: Snapshot | null = null;
let inFlight: { uid: string; promise: Promise<string[]> } | null = null;
/** Bumped by every reset. A read captures it before going out and refuses to
 *  write the memo if it changed while the request was open — otherwise a read
 *  that RESOLVES after sign-out re-seeds the memo for the next session. */
let generation = 0;

/** Drop the memo AND the published state — BOTH halves, the set and the identity. Called on
 *  sign-out and session end (authz metadata must not outlive the session) and between tests.
 *  `uid: null` afterwards is also what `useRBAC` reads as "no answer for this user yet".
 *
 *  ⚠ The identity is cleared HERE rather than by its own exported reset, so that the three live
 *  callers (`AuthProvider` ×2, `useAuth`) cannot clear one half and leave the other: a surviving
 *  identity with no set is the fail-closed state, and a surviving SET with no identity would
 *  render as "holds nothing" for the next person to sign in on this device. */
export function resetMePermissions(): void {
  snapshot = null;
  inFlight = null;
  generation += 1;
  publish(NO_ANSWER);
  clearIdentity();
}

/** Adopt a /users/me response from provisioning, superseding older reads.
 * A response without an array leaves this uid's published live/cache answer untouched. */
export function acceptMePermissions(uid: string, permissions: unknown): void {
  if (!Array.isArray(permissions)) return;
  const resolved = permissions.filter((p): p is string => typeof p === 'string');
  generation += 1;
  inFlight = null;
  snapshot = { uid, permissions: resolved, at: Date.now() };
  publish({ uid, permissions: resolved, source: 'live', unavailable: false, failure: null });
}

/**
 * The caller's effective permissions, and the published answer every other
 * consumer will render.
 *
 * One argument since P4: the ID token no longer carries a permission fallback. Rejects if
 * /users/me is unreachable after one bounded retry or if it answers with no set. The caller may
 * publish this user's uid-keyed offline cache; this function never invents an empty set.
 */
export async function fetchMePermissions(uid: string): Promise<string[]> {
  const now = Date.now();
  if (snapshot && snapshot.uid === uid && now - snapshot.at < ME_PERMISSIONS_TTL_MS) {
    return snapshot.permissions;
  }
  if (inFlight && inFlight.uid === uid) return inFlight.promise;

  const gen = generation;
  /** Publish only while this read still belongs to the live session. */
  const fail = (failure: PermissionFailureClass) => {
    if (gen === generation) publishPermissionsUnavailable(uid, failure);
  };
  const promise = (async () => {
    let permissions: string[] | null;
    try {
      permissions = await readPermissions();
    } catch (error) {
      // ONE retry, then give up. The caller fails CLOSED on a rejection, and a
      // fail-closed set is indistinguishable from "this user holds nothing" —
      // so a single dropped request used to read as a total permission loss for
      // every already-mounted consumer, with nothing but a console.error to say
      // otherwise. One retry absorbs that blip class; two would be a caller
      // waiting on a backend that is genuinely down, which the shell surfaces
      // instead (useRBAC's `permissionsUnavailable`). When the store ALREADY holds
      // an answer for this uid, a transient give-up keeps it rather than blanking
      // the screen — see `publishPermissionsUnavailable`.
      // ADR-0017: "the password is due" is not an answer about this user, and a retry with the
      // same token gets the same refusal. Publish nothing; AuthProvider re-reads after the re-auth.
      if (isSessionExpiredError(error)) throw error;
      if (!isTransient(error)) {
        fail('definitive');
        throw error;
      }
      await delay(ME_PERMISSIONS_RETRY_DELAY_MS);
      try {
        permissions = await readPermissions();
      } catch (retryError) {
        fail(isTransient(retryError) ? 'transient' : 'definitive');
        throw retryError;
      }
    }
    const source: PermissionSource = 'live';
    if (permissions === null) {
      // Definitive since P4: there is no token-borne permissions claim left to consult.
      fail('definitive');
      throw new Error('GET /users/me carried no permissions array');
    }
    // A reset (sign-out / session end) while this was open ends the session this
    // answer belongs to. Still answer THIS caller; never memoise or publish it.
    if (gen === generation) {
      snapshot = { uid, permissions, at: Date.now() };
      publish({ uid, permissions, source, unavailable: false, failure: null });
    }
    return gen !== generation && state.uid === uid && state.permissions !== null
      ? state.permissions : permissions;
  })();

  // Log the failure HERE, once per read, rather than in the hook: `useRBAC` has
  // ~45 consumers and they share this promise, so a single blip used to print ~45
  // identical lines. The derived promise swallows nothing — `promise` is what the
  // callers get, and it still rejects.
  promise.catch((error) => {
    console.error('Error fetching effective permissions from /users/me:', error);
  });

  const entry = { uid, promise };
  inFlight = entry;
  try {
    return await promise;
  } finally {
    if (inFlight === entry) inFlight = null;
  }
}
